import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './Ventilation.css';

const products = [
  {
    name: "Air Cooler",
    description: "Efficient cooling with advanced evaporative technology, ideal for large areas. Our air coolers are designed with a focus on both performance and sustainability, making them perfect for spaces that require consistent and natural airflow. The cooler’s robust construction ensures durability and longevity, while the advanced honeycomb cooling pads maximize cooling efficiency. Key features include a powerful fan, large water tank capacity for extended use, and a customizable speed setting to suit different comfort levels. Ideal for homes, workshops, or any open space needing a refreshing breeze, this cooler is a blend of convenience, cost-effectiveness, and environmental care.",
    main360Image: 'https://via.placeholder.com/300/FFCC00/FFFFFF?text=360+View',
    additionalImages: [
      require('../../../assets/cool breeze 3.png'),
      require('../../../assets/cool breeze 3.png'),
      require('../../../assets/cool_breeze.png'),
    ],
    videoLink: 'https://www.w3schools.com/html/mov_bbb.mp4'
  },
  {
    name: "HVLS Fans",
    description: "High-volume, low-speed fans for optimal airflow in spacious areas. Designed for high ceilings and expansive spaces, our HVLS (High-Volume, Low-Speed) fans are engineered to move large volumes of air with minimal energy consumption. The fan's aerodynamic blades ensure smooth and continuous air distribution, reducing perceived temperature and enhancing comfort. It’s ideal for industrial warehouses, large halls, gyms, and commercial spaces. In addition, our HVLS fan helps in energy savings by reducing the load on air conditioning systems. Quiet, efficient, and built for longevity, it’s a powerful solution to keep large areas comfortably ventilated.",
    main360Image: 'https://via.placeholder.com/300/007ACC/FFFFFF?text=360+View',
    additionalImages: [
      require('../../../assets/hvls.jpg'),
      require('../../../assets/hvls_png.png')
    ],
    videoLink: 'https://www.w3schools.com/html/mov_bbb.mp4'
  },
  {
    name: "Exhaust Fans",
    description: "Our Exhaust Fans are designed to effectively remove stale air and unwanted moisture from indoor spaces, ensuring optimal air quality and comfort. Perfect for kitchens, bathrooms, and industrial environments, these fans feature high-volume air movement capabilities that help to quickly ventilate areas and reduce humidity levels. With an efficient, low-noise operation and durable construction, our exhaust fans are engineered to enhance airflow while minimizing energy consumption. Whether for residential or commercial applications, they provide a reliable solution for maintaining fresh, clean air indoors.",
    main360Image: 'https://via.placeholder.com/300/007ACC/FFFFFF?text=360+View',
    additionalImages: [
      require('../../../assets/exhaust.jpg'),
      require('../../../assets/exhaust_pic.png')
    ],
    videoLink: 'https://www.w3schools.com/html/mov_bbb.mp4'
  },
  {
    name: "Air Handling Unit",
    description: "The Air Handling Unit (AHU) is a critical component of HVAC systems, responsible for regulating and circulating air throughout commercial and industrial spaces. Our AHUs are designed for high efficiency and versatility, allowing for customizable airflow, filtration, and temperature control. Featuring robust construction and advanced fan technology, they are capable of handling large volumes of air while ensuring energy efficiency. Ideal for large buildings, such as offices, hospitals, and schools, our air handling units help create comfortable and healthy indoor environments by delivering fresh, conditioned air.",
    main360Image: 'https://via.placeholder.com/300/007ACC/FFFFFF?text=360+View',
    additionalImages: [
      require('../../../assets/air_handling.png')
    ],
    videoLink: 'https://www.w3schools.com/html/mov_bbb.mp4'
  },
];

const Ventilation = () => {
  const initialProductStates = products.map((product) => ({
    selectedImage: product.main360Image,
    isVideo: false,
  }));

  const [productStates, setProductStates] = useState(initialProductStates);

  
  useEffect(() => {
    if (productStates.length !== products.length) {
      setProductStates(initialProductStates);
    }
  }, [productStates, products, initialProductStates]);

  const handleVideoClick = (index) => {
    setProductStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, isVideo: true, selectedImage: null } : state
      )
    );
  };

  const handleImageClick = (image, index) => {
    setProductStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, selectedImage: image, isVideo: false } : state
      )
    );
  };

  return (
    <div className="ventilation-container">
      {products.map((product, index) => (
        <motion.div
          key={index}
          className={`product-section ${product.name.toLowerCase().replace(" ", "-")}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: index * 0.4 }}
        >
          <motion.h1 
            className="product-title" 
            initial={{ scale: 0.8, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
          >
            {product.name}
          </motion.h1>
          <div className="product-content">
            <div className="thumbnail-section">
              {product.additionalImages.map((image, i) => (
                <motion.img
                  key={i}
                  src={image}
                  alt={`${product.name} view ${i + 1}`}
                  className={`thumbnail ${productStates[index]?.selectedImage === image ? 'active' : ''}`}
                  onClick={() => handleImageClick(image, index)}
                  whileHover={{ scale: 1.15 }}
                  transition={{ duration: 0.3 }}
                />
              ))}
              <motion.button
                className="video-button"
                onClick={() => handleVideoClick(index)}
                whileHover={{ scale: 1.1, backgroundColor: "#f0c14b", color: "#111" }}
                transition={{ duration: 0.3 }}
              >
                Watch Video
              </motion.button>
            </div>
            <motion.div 
              className="main-image-container" 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 100, delay: 0.4 }}
            >
              {productStates[index]?.isVideo ? (
                <motion.video
                  className="current-video"
                  controls
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <source src={product.videoLink} type="video/mp4" />
                  Your browser does not support the video tag.
                </motion.video>
              ) : (
                <motion.img
                  src={productStates[index]?.selectedImage || product.main360Image}
                  alt={`${product.name} main view`}
                  className="current-frame"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
              )}
            </motion.div>
            <motion.div 
              className="product-text"
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <p>{product.description}</p>
            </motion.div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default Ventilation;